import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { Checkbox } from 'antd';
import { BANNER_WIDGET_ITEM_LINK_TYPE } from '@amondz/constants';

import Form from '../../../../components/form/Form';
import Panel from '../../../../components/Panel';
import Select from '../../../../components/input/Select';
import Notice from '../../../../components/Notice';
import LinkId from '../../../../components/input/LinkId';
import SearchListTrigger from '../../../../components/SearchListTrigger';
import { SlideImageUpload } from './components/SlideImageUpload';
import { SlidePeriodPicker } from './components/SlidePeriodPicker';
import UrlInput from '../../../widget/detail/WidgetForm/BannerWidgetForm/UrlInput';
import { CATEGORY_BANNER_LINK_TYPE_SELECT_LIST } from './constants';
import { ImagePairStyle } from './SlideTabContent.style';
import { CategoryBannerSlideFormItem, InnerRules, LinkItem } from './types';
import { EVENT, PRODUCT } from '../../../../constants/queryKeys';
import { eventListFetcher } from '../../../widget/detail/common/fetchers/eventListFetcher';
import { productListFetcher } from '../../../widget/detail/common/fetchers/productListFetcher';
import { eventReactTableColumns, productReactTableColumns } from '../../../widget/detail/common/columns';
import { EventItemV2 } from '../../../../types/event';
import { ProductItemV2 } from '../../../../types/product';
import { textNormalize } from '../../../../lib/utils/form/normalize';

type SlideTabContentProps = {
  item: CategoryBannerSlideFormItem;
  tabForm: WrappedFormUtils;
  innerRules: InnerRules;
};

export const SlideTabContent = ({ item, tabForm, innerRules }: SlideTabContentProps) => {
  const renderLinkId = () => {
    const trigger = (openModal: () => void) => (
      <SearchListTrigger
        value={item.link}
        triggerText="ID 선택"
        linkText={item.link ? `${item.link.name}(${item.link.id})` : ''}
        onClick={openModal}
      />
    );

    switch (item.linkType) {
      case BANNER_WIDGET_ITEM_LINK_TYPE.EVENT:
        return (
          <LinkId
            queryKey={EVENT.LIST}
            fetcher={eventListFetcher}
            columns={eventReactTableColumns}
            formatter={(event: EventItemV2): LinkItem => ({ id: event.id, name: event.mainTitle })}
            trigger={trigger}
          />
        );
      case BANNER_WIDGET_ITEM_LINK_TYPE.PRODUCT_DETAIL:
        return (
          <LinkId
            queryKey={PRODUCT.LIST}
            fetcher={productListFetcher}
            columns={productReactTableColumns}
            formatter={(product: ProductItemV2): LinkItem => ({ id: product.id, name: product.name })}
            trigger={trigger}
          />
        );
      default:
        // linkType이 EXTERNAL_LINK(WEB)인 경우엔 Panel.Section hideIf로 가려지므로 이 분기는 실질적으로 도달하지 않음
        return null;
    }
  };

  return (
    <>
      <Panel.Section>
        <Form.Item
          label="배너 이미지"
          required
          notice={<Notice>* 모바일/PC 이미지를 각각 등록해주세요. 권장 크기보다 작을 경우 화질 저하의 우려가 있습니다.</Notice>}
        >
          <ImagePairStyle>
            <div className="image-column">
              <div className="column-header">모바일</div>
              <Form.Item name="mobileImage" rules={innerRules.mobileImage.rules}>
                <SlideImageUpload />
              </Form.Item>
              <div className="size-guide">
                * 노출 크기: 375 x 136 px / 권장 해상도: 750 x 272 px (x2)
                <br />* 화질 저하 방지를 위해 노출 크기의 x2 이상 해상도를 권장합니다.
              </div>
            </div>
            <div className="image-column">
              <div className="column-header">PC</div>
              <Form.Item name="pcImage" rules={innerRules.pcImage.rules}>
                <SlideImageUpload />
              </Form.Item>
              <div className="size-guide">
                * 노출 크기: 719 x 139 px / 권장 해상도: 1438 x 278 px (x2)
                <br />* 화질 저하 방지를 위해 노출 크기의 x2 이상 해상도를 권장합니다.
              </div>
            </div>
          </ImagePairStyle>
        </Form.Item>
      </Panel.Section>
      <Panel.Section>
        <Form.Item
          name="exposurePeriod"
          label="노출 기간"
          required
          rules={innerRules.exposurePeriod.rules}
        >
          <SlidePeriodPicker />
        </Form.Item>
      </Panel.Section>
      <Panel.Section>
        <Form.Item name="isNonLink" label="링크" valuePropName="checked" initialValue={false}>
          {/* 체크/해제 시 link/linkUrl 값을 보존해 재토글 시 이전 입력이 복원되도록 한다. */}
          {/* payload는 isNonLink만 보고 결정되고, formRules도 isNonLink=true면 rules를 skip하므로 안전. */}
          <Checkbox>링크 없음</Checkbox>
        </Form.Item>
      </Panel.Section>
      {/* "링크 없음" 체크 시 링크 섹션 전체를 CSS로만 감춘다.
          Panel.Section hideIf는 DOM unmount라 compatible Form이 필드 값을 잃으므로,
          체크 토글만으로 link/linkUrl이 증발하지 않도록 외곽 div의 display로만 제어한다.
          linkType 간 전환은 원래대로 Panel.Section hideIf로 unmount/remount. */}
      <div style={{ display: item.isNonLink ? 'none' : undefined }}>
        <Panel.Section>
          <Form.Item
            name="linkType"
            label="링크 종류"
            initialValue={BANNER_WIDGET_ITEM_LINK_TYPE.EVENT}
          >
            <Select
              list={CATEGORY_BANNER_LINK_TYPE_SELECT_LIST}
              style={{ width: 250 }}
              onChange={() => {
                tabForm.setFieldsValue({ link: null, linkUrl: '' });
              }}
            />
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={innerRules.link.hideIf}>
          <Form.Item
            name="link"
            label="링크 ID"
            required
            rules={innerRules.link.rules}
            notice={
              <Notice>
                * 링크 종류가 '기획전 상세 페이지', '상품 상세 페이지'인 경우 링크 ID를 반드시 설정하세요.
              </Notice>
            }
          >
            {renderLinkId()}
          </Form.Item>
        </Panel.Section>
        <Panel.Section hideIf={innerRules.linkUrl.hideIf}>
          <Form.Item
            name="linkUrl"
            label="웹 페이지"
            required
            rules={innerRules.linkUrl.rules}
            normalize={textNormalize}
            notice={<Notice>* 링크 종류가 '외부 웹페이지'인 경우 URL을 반드시 입력하세요.</Notice>}
          >
            <UrlInput canCategorySearch={false} />
          </Form.Item>
        </Panel.Section>
      </div>
    </>
  );
};
